import { useFeatureDetectionContext } from "@/components/common/feature-detection-context";
import { useCached3DObjectsIfReady } from "@/object-cache";
import { isPanoObject, isPointCloudObject } from "@/object-cache-type-guard";
import {
  PanoMeasurementToolState,
  setPanoMeasurementToolState,
} from "@/store/measurement-tool-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { selectActiveTool } from "@/store/ui/ui-selectors";
import { ToolName } from "@/store/ui/ui-slice";
import { ClipSceneTool, ClipSceneToolProps } from "@/tools/clip-scene-tool";
import { PanoMeasurementTool } from "@/tools/pano-measurement-tool";
import {
  PickingTools,
  PickingToolsProps,
  PickingToolsRef,
} from "@/tools/picking-tools";
import {
  PointerMovePreviewHandler,
  PointerMovePreviewHandlerRef,
  usePointerMovePreview,
} from "@/tools/pointer-move-preview-handler";
import { isValid } from "@faro-lotv/ielement-types";
import {
  forwardRef,
  RefObject,
  useEffect,
  useMemo,
  useState,
  type JSX,
} from "react";
import { WalkSceneActiveElement, WalkSceneOverlayElement } from "./walk-types";

export type WalkToolsProps = Pick<PickingToolsProps, "onActiveToolChanged"> & {
  /** The current element of the scene */
  currentElement: WalkSceneActiveElement;
  /** The element that we want to render together with the current element */
  overlayElement?: WalkSceneOverlayElement;
  /** A reference to the pointer move preview component */
  pointerMovePreview: RefObject<PointerMovePreviewHandlerRef | null>;
} & ClipSceneToolProps;

/** @returns the logic to controls what tool is active in Walk Mode */
export const WalkTools = forwardRef<PickingToolsRef, WalkToolsProps>(
  function WalkTools(
    {
      currentElement,
      overlayElement,
      onActiveToolChanged,
      areaBox,
      modelBox,
      clippingBoxChanging,
      clippingPlanesChanged,
      pointerMovePreview,
    },
    ref,
  ): JSX.Element | null {
    const activeTool = useAppSelector(selectActiveTool);
    usePointerMovePreview(activeTool);

    const activeElements = useWalkModeActiveElements(
      currentElement,
      overlayElement,
    );
    const activeModels = useCached3DObjectsIfReady(activeElements, "WalkTools");

    const showPointerMovePreview = useFeatureDetectionContext().normalDetection;
    const pointCloud = useMemo(
      () => activeModels.find((e) => isPointCloudObject(e)),
      [activeModels],
    );

    const pano = useMemo(
      () => activeModels.find((e) => isPanoObject(e)),
      [activeModels],
    );
    const dispatch = useAppDispatch();

    useEffect(() => {
      // reset pano measurement tool when switching to another tool
      if (activeTool !== ToolName.measurement) {
        dispatch(setPanoMeasurementToolState(PanoMeasurementToolState.None));
      }
    }, [activeTool, dispatch]);

    useEffect(() => {
      // reset pano measurement tool when switching to another pano
      dispatch(setPanoMeasurementToolState(PanoMeasurementToolState.None));
    }, [dispatch, pano]);

    const [enableControls, setEnableControls] = useState(true);

    return (
      <>
        <PickingTools
          activeModels={activeModels}
          enableControls={enableControls}
          onActiveToolChanged={onActiveToolChanged}
          ref={ref}
        />
        <ClipSceneTool
          active={activeTool === ToolName.clipScene}
          areaBox={areaBox}
          modelBox={modelBox}
          clippingBoxChanging={clippingBoxChanging}
          clippingPlanesChanged={clippingPlanesChanged}
        />
        {showPointerMovePreview && (
          <PointerMovePreviewHandler
            pointcloud={pointCloud}
            ref={pointerMovePreview}
          />
        )}
        {activeTool === ToolName.measurement && (
          <PanoMeasurementTool
            pano={pano}
            setEnableControls={setEnableControls}
          />
        )}
      </>
    );
  },
);

/**
 * @returns The list of elements for which the picking tools are enabled
 * @param currentElement The current element of the scene
 * @param overlayElement The overlay element of the scene
 */
export function useWalkModeActiveElements(
  currentElement: WalkSceneActiveElement,
  overlayElement?: WalkSceneOverlayElement,
): WalkSceneActiveElement[] | undefined {
  return useMemo(
    () => [currentElement, overlayElement].filter(isValid),
    [currentElement, overlayElement],
  );
}
